import { UserRole } from '@/lib/types';

export enum ERoute {
  DASHBOARD = 'dashboard',
  ACCOUNT = 'account',
  PROFILE = 'profile',
  PAYMENT_HISTORY = 'paymenthistory',
  SETTINGS = 'settings',
  ANIMALS = 'animals',
  MEMBERS = 'members',
  RULES = 'rules',
  ADD_ANIMAL = 'addanimal',
  RESTRICTED = 'restricted',
  ORGANISATION = 'organisation',
  ADD_ORG_TRAIT = 'addorganisationtrait',
  CONFIG = 'config',
  BECOME_MEMBER = 'becomemember',
  ADD_MEMBER = 'add-member/details',
  BUILDER = 'builder',
  FINISH_ACCOUNT = 'finish-account-setup',
  FIELDS_CONFIG = 'fields',
  FORMS_MANAGER = 'forms',
  SOCIETY_SETTINGS = 'society-settings',
  TRANSFERS = 'transfers',
  IMPORTS = 'imports',
  CERTIFICATE_TEMPLATES = 'certificates',
  SOLR_SYNC = 'solr-sync',
}

export const APP_ROUTE_NAMES = {
  HOME: 'Home',
  SEARCH: 'Database Search',
  REGISTRY: 'Registry',
};

export const PRIVATE_TOP_LEVEL_SEGMENTS: readonly string[] = [
  'dashboard',
  'pendingorders',
  'account',
  'entity-query',
  'restricted',
];

export const PUBLIC_ROUTES = {
  HOME: '/',
  /** Org-picker landing (public marketing header). */
  SEARCH: '/search',
  ACCEPT_INVITE: '/accept-invite',
  HEALTHZ: '/healthz',
  LOGOUT: '/logout',
  RESTRICTED: '/restricted',
  SUPPORT: '/support',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  /**
   * Friendly fallback page for auth-flow failures (e.g. misconfigured
   * Entra/OIDC env vars). Linked to by the auth route handler when a
   * browser navigation hits an unrecoverable auth-side error.
   */
  AUTH_ERROR: '/auth-error',
  /** Society public animal search + detail: `/{societyCode}/animals`. */
  ANIMAL: '/animals',
  /** Society public member search + detail: `/{societyCode}/members`. */
  MEMBER: '/members',
  REGISTRY_LANDING: '/registry',
};

export const APP_ROUTE_PATHS = {
  DASHBOARD: `/${ERoute.DASHBOARD}`,
  FORMS_MANANGER: `/${ERoute.CONFIG}/${ERoute.FORMS_MANAGER}`,
  CREATE_FORM: `/${ERoute.CONFIG}/${ERoute.FORMS_MANAGER}/create`,
  FIELDS_CONFIG: `/${ERoute.CONFIG}/${ERoute.FIELDS_CONFIG}`,
  SOCIETY_SETTINGS: `/${ERoute.CONFIG}/${ERoute.SOCIETY_SETTINGS}`,
  ANIMALS: `/${ERoute.ANIMALS}`,
  IMPORTS: `/${ERoute.IMPORTS}`,
  MEMBERS: `/${ERoute.MEMBERS}`,
  RULES: `/${ERoute.CONFIG}/${ERoute.RULES}`,
  RULE_TEST_RUNNER: `/${ERoute.CONFIG}/${ERoute.RULES}/test-runner`,
  CREATE_ANIMAL: `/${ERoute.ANIMALS}/create`,
  CREATE_MEMBER: `/${ERoute.MEMBERS}/create`,
  ADD_MEMBER: `/${ERoute.MEMBERS}/${ERoute.ADD_MEMBER}`,
  TRANSFERS: `/${ERoute.ANIMALS}/${ERoute.TRANSFERS}`,
  CERTIFICATE_TEMPLATES: `/${ERoute.CONFIG}/${ERoute.CERTIFICATE_TEMPLATES}`,
  SOLR_SYNC: `/${ERoute.SOLR_SYNC}`,
};

/**
 * Per-segment role requirements for top-level registry routes
 * (`/[orgName]/registry/<segment>/...`).
 *
 * Segments that are not listed here are accessible to any member of the org.
 * To restrict a new registry route, add it here.
 */
export const REGISTRY_SEGMENT_PERMISSIONS: Partial<
  Record<ERoute, ReadonlyArray<UserRole>>
> = {
  // Society admins and Breedlogic admins can manage org-level config.
  [ERoute.CONFIG]: [UserRole.SocietyAdmin, UserRole.Admin],
};

/**
 * Registry segments that require ABRI staff (not a society role).
 */
export const REGISTRY_SEGMENT_REQUIRES_ABRI_STAFF: ReadonlySet<ERoute> =
  new Set([ERoute.SOLR_SYNC]);

function getRegistryPathSegment(
  pathname: string,
  orgName: string,
): ERoute | null {
  const prefix = `/${orgName}/registry/`;
  if (!pathname.startsWith(prefix)) return null;
  return pathname.slice(prefix.length).split('/')[0] as ERoute;
}

export const registryPathRequiresAbriStaff = (
  pathname: string,
  orgName: string,
): boolean => {
  const segment = getRegistryPathSegment(pathname, orgName);
  return segment != null && REGISTRY_SEGMENT_REQUIRES_ABRI_STAFF.has(segment);
};

/**
 * Returns the roles allowed to access a given registry pathname, or `null`
 * if the path has no restriction (or isn't under the registry prefix).
 */
export const getRegistryPathAllowedRoles = (
  pathname: string,
  orgName: string,
): ReadonlyArray<UserRole> | null => {
  const segment = getRegistryPathSegment(pathname, orgName);
  if (segment == null) return null;
  return REGISTRY_SEGMENT_PERMISSIONS[segment] ?? null;
};

/**
 * Returns true if a user holding any of the given `roleIds` is allowed to
 * access the registry `pathname`. Unrestricted paths return true.
 *
 * Accepts a list because a user may hold several roles in one org (e.g. a
 * Breedlogic Admin granted both via wildcard and as Society Admin); the
 * single "primary" role id can hide the higher-privilege one.
 *
 * For backwards compatibility a single id is also accepted.
 */
export const canAccessRegistryPath = (
  pathname: string,
  orgName: string,
  roleIds: number | number[] | null | undefined,
): boolean => {
  const allowed = getRegistryPathAllowedRoles(pathname, orgName);
  if (!allowed) return true;

  const ids =
    roleIds == null ? [] : Array.isArray(roleIds) ? roleIds : [roleIds];

  return ids.some((id) => allowed.includes(id as UserRole));
};
